import { Stack, Progress, List, Text, ThemeIcon, Paper, Group } from "@mantine/core"
import { IconCheck, IconX } from "@tabler/icons-react"

interface PasswordRequirement {
  label: string
  meets: (password: string) => boolean
}

const requirements: PasswordRequirement[] = [
  {
    label: "At least 8 characters",
    meets: (password) => password.length >= 8,
  },
  {
    label: "Contains at least one uppercase letter",
    meets: (password) => /[A-Z]/.test(password),
  },
  {
    label: "Contains at least one lowercase letter",
    meets: (password) => /[a-z]/.test(password),
  },
  {
    label: "Contains at least one number",
    meets: (password) => /[0-9]/.test(password),
  },
  {
    label: "Contains at least one special character",
    meets: (password) => /[^A-Za-z0-9]/.test(password),
  },
]

function getStrength(password: string) {
  if (password.length === 0) return 0
  const metRequirements = requirements.filter((req) => req.meets(password)).length
  return metRequirements * 20 // Each requirement is worth 20%
}

interface PasswordStrengthProps {
  inPopover?: boolean
  password: string
}

export function PasswordStrength({ inPopover = false, password }: PasswordStrengthProps) {
  const strength = getStrength(password)
  const metRequirements = strength / 20 // Number of requirements met

  const getColor = () => {
    switch (metRequirements) {
      case 0:
        if (password.length === 0) {
          return "gray"
        }
        return "red"
      case 1:
        return "red"
      case 2:
        return "orange"
      case 3:
        return "yellow"
      case 4:
        return "lime"
      case 5:
        return "green"
      default:
        return "red"
    }
  }
  const color = getColor()
  const compactRequirementsList = (
    <Stack gap="xs">
      {requirements.map((requirement, index) => {
        const isMet = requirement.meets(password)
        return (
          <Group key={index} gap="sm" wrap="nowrap">
            <ThemeIcon color={isMet ? "green" : "red"} variant="light" size="sm" radius="xl">
              {isMet ? <IconCheck size={14} /> : <IconX size={14} />}
            </ThemeIcon>
            <Text size="sm" c="dimmed" td={isMet ? "line-through" : "none"}>
              {requirement.label}
            </Text>
          </Group>
        )
      })}
    </Stack>
  )
  const requirementsList = inPopover ? (
    compactRequirementsList
  ) : (
    <List spacing="xs" size="sm" center>
      {requirements.map((requirement, index) => {
        const isMet = requirement.meets(password)
        return (
          <List.Item
            key={index}
            icon={
              <ThemeIcon color={isMet ? "green" : "red"} variant="light" size="sm" radius="xl">
                {isMet ? <IconCheck size={14} /> : <IconX size={14} />}
              </ThemeIcon>
            }
          >
            <Text size="sm" c="dimmed" td={isMet ? "line-through" : "none"}>
              {requirement.label}
            </Text>
          </List.Item>
        )
      })}
    </List>
  )

  const getLabel = () => {
    switch (metRequirements) {
      case 0:
        if (password.length === 0) {
          return "Password requirements:"
        }
        return "Very weak"
      case 1:
        return "Very weak"
      case 2:
        return "Weak"
      case 3:
        return "Medium"
      case 4:
        return "Strong"
      case 5:
        return "Very strong"
      default:
        return "Very weak"
    }
  }

  return (
    <Stack gap="sm" mt={inPopover ? 0 : "md"}>
      <Group grow gap={4}>
        <Progress size={inPopover ? "md" : "xs"} color={color} value={metRequirements >= 1 ? 100 : 0} transitionDuration={0} radius="xl" />
        <Progress size={inPopover ? "md" : "xs"} color={color} value={metRequirements >= 2 ? 100 : 0} transitionDuration={0} radius="xl" />
        <Progress size={inPopover ? "md" : "xs"} color={color} value={metRequirements >= 3 ? 100 : 0} transitionDuration={0} radius="xl" />
        <Progress size={inPopover ? "md" : "xs"} color={color} value={metRequirements >= 4 ? 100 : 0} transitionDuration={0} radius="xl" />
        <Progress size={inPopover ? "md" : "xs"} color={color} value={metRequirements >= 5 ? 100 : 0} transitionDuration={0} radius="xl" />
      </Group>
      {inPopover ? null : (
        <Text size="xs" c={color} fw={500}>
          {getLabel()}
        </Text>
      )}
      {inPopover ? (
        requirementsList
      ) : (
        <Paper shadow="none" withBorder p="md" bg="gray.0" radius="md">
          {requirementsList}
        </Paper>
      )}
    </Stack>
  )
}
